import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styles from './MicrositeCustomPageEditor.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import TextInput from '../components/TextInput/TextInput'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeDateTimePicker from '../components/MicrositeDateTimePicker/MicrositeDateTimePicker'
import MicrositeBlockEditor from '../components/MicrositeBlockEditor/MicrositeBlockEditor'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'

const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'scheduled', label: 'Scheduled' }
]

const EMPTY_FORM = {
    pageTitle: '',
    pageSlug: '',
    status: 'draft',
    publishAt: '',
    metaTitle: '',
    metaDescription: '',
    blocks: []
}

const slugify = (text) =>
    text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-')

const MicrositeCustomPageEditor = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const isNew = id === 'new' || !id

    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [slugTouched, setSlugTouched] = useState(false)

    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadExisting = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetCustomPageDetail', { ID: id })
        if (res?.status === 'success') {
            const p = res.response || {}
            const next = {
                pageTitle: p.pageTitle || '',
                pageSlug: p.pageSlug || '',
                status: p.status || 'draft',
                publishAt: p.publishAt ? p.publishAt.replace(' ', 'T').slice(0, 16) : '',
                metaTitle: p.metaTitle || '',
                metaDescription: p.metaDescription || '',
                blocks: p.content?.blocks || []
            }
            setForm(next)
            setOriginal(next)
            setSlugTouched(true)
        } else {
            showToast('Could not load page. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post, id])

    useEffect(() => {
        if (!isNew && activeStore?.storeIdentifier) loadExisting()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, id])

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    const handleTitleChange = (value) => {
        update('pageTitle', value)
        if (!slugTouched) {
            update('pageSlug', '/' + slugify(value))
        }
    }

    const handleSave = async () => {
        if (!form.pageTitle.trim() || !form.pageSlug.trim()) {
            showToast('Page title and slug are required', 'error')
            return
        }
        setSaving(true)

        const payload = {
            pageTitle: form.pageTitle,
            pageSlug: form.pageSlug,
            status: form.status,
            publishAt: form.publishAt ? form.publishAt.replace('T', ' ') + ':00' : '',
            metaTitle: form.metaTitle,
            metaDescription: form.metaDescription,
            content: { blocks: form.blocks }
        }

        const res = isNew
            ? await post('micrositeCreateCustomPage', payload)
            : await post('micrositeUpdateCustomPage', { ID: id, ...payload })

        setSaving(false)

        if (res?.status === 'success') {
            showToast('Page saved successfully')
            setOriginal(form)
            navigate('/microsite/pages')
        } else {
            showToast('Could not save page. Please try again.', 'error')
        }
    }

    const handleDelete = async () => {
        const res = await post('micrositeDeleteCustomPage', { ID: id })
        if (res?.status === 'success') {
            showToast('Page deleted')
            navigate('/microsite/pages')
        } else {
            showToast('Could not delete page', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading page…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title={isNew ? 'New custom page' : 'Edit page'}
                backTo="/microsite/pages"
                backLabel="Custom pages"
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onDelete={!isNew ? () => setDeleteConfirmOpen(true) : undefined}
            />

            <section className={styles.fieldGroup}>
                <TextInput
                    label="Page title"
                    value={form.pageTitle}
                    onChange={handleTitleChange}
                    placeholder="e.g. Cancellation Policy"
                    required
                />
                <TextInput
                    label="URL slug"
                    value={form.pageSlug}
                    onChange={(v) => { setSlugTouched(true); update('pageSlug', v) }}
                    placeholder="/cancellation-policy"
                    required
                />

                <div className={styles.row2}>
                    <SelectInput
                        label="Status"
                        value={form.status}
                        onChange={(v) => update('status', v)}
                        options={STATUS_OPTIONS}
                    />
                    {form.status === 'scheduled' && (
                        <MicrositeDateTimePicker
                            label="Publish at"
                            value={form.publishAt}
                            onChange={(v) => update('publishAt', v)}
                        />
                    )}
                </div>
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Content</h2>
                <p className={styles.groupDesc}>Build your page using text, image, and button blocks.</p>
                <MicrositeBlockEditor blocks={form.blocks} onChange={(v) => update('blocks', v)} />
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>SEO (optional)</h2>
                <TextInput
                    label="Meta title"
                    value={form.metaTitle}
                    onChange={(v) => update('metaTitle', v)}
                    placeholder="Defaults to page title if left blank"
                />
                <TextInput
                    label="Meta description"
                    value={form.metaDescription}
                    onChange={(v) => update('metaDescription', v)}
                    placeholder="A short description for search results"
                    maxLength={160}
                    showCharCount
                />
            </section>

            <MicrositeDeleteConfirm
                mode="modal"
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
                itemLabel="this page"
            />
        </div>
    )
}

export default MicrositeCustomPageEditor